import React from "react";
import _ from "lodash";
import moment from "moment";
import submit from "submit-form";
import getDayRangeString from "../../../utils/day-range-string";
import Chart from "../../../parts/chart";
import DateInput from "../../../parts/inputs/date-input";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import ShareModal from "../../../parts/modals/share";
import MetricsFoot from "./MetricsFoot";
import MetricsBackButton from "./metrics-back-button";
import set from "@rill/set";

import { PERIOD_FORMATS, DATE_FORMATS } from "../../../constants";
import ActivityDrawer from "../../../parts/activity-drawer/ActivityDrawer";
import { useRillContext } from "../../../rill/RillReact/share/base";

const { DATE_PICKER_FORMAT } = DATE_FORMATS;

function View(props) {
  const { locals } = useRillContext();
  const { exercisesVolumeSplitRawEquipped, query, search } = locals;
  const { period, units } = query;
  const organizationCoach = query.organizationCoach || undefined;

  const period_short = PERIOD_FORMATS[period];
  const selectedMetrics =
    query.type === "total"
      ? exercisesVolumeSplitRawEquipped
      : _.pick(exercisesVolumeSplitRawEquipped, [query.type]);
  const keys = _(selectedMetrics)
    .flatMap(_.keys)
    .uniq()
    .map(Number)
    .sortBy()
    .value();
  const labels = _.map(keys, (i) => `${period_short}${i + 1}`);
  const selectedMetricValues = _.values(selectedMetrics);

  const rawSeries = {
    name: "raw",
    className: "raw",
    data: _.map(keys, (key) => {
      return locals.fromMetric(
        _.sumBy(selectedMetricValues, `${key}.raw`),
        units
      );
    }),
  };

  const equippedSeries = {
    name: "equipped",
    className: "equipped",
    data: _.map(keys, (key) => {
      return locals.fromMetric(
        _.sumBy(selectedMetricValues, `${key}.equipped`),
        units
      );
    }),
  };

  const totalSeries = _.zipWith(
    rawSeries.data,
    equippedSeries.data,
    (a = 0, b = 0) => a + b
  );
  const volumeSeries = _.concat(rawSeries, equippedSeries);
  const maxVolume = _.max(totalSeries);

  return (
    <div className="Main Metrics">
      <MetricsBackButton url={`/athlete-portal/metrics/${search}`} />
      <h1 className="raw-equip-vol-split alt">Raw & Equipped Volume Split</h1>
      <span className="description">
        Analyze the split of your volume between raw and equipped lifts.
      </span>

      <form id="metrics-form" action="#_" method="GET" data-noreset>
        <input type="hidden" name="athlete" value={locals.athlete._id}></input>
        <input
          type="hidden"
          name="organizationCoach"
          value={organizationCoach ? organizationCoach : ""}
        ></input>
        <div className="Date-Range">
          <div className="left-side">
            <span className="label">Reporting Period</span>
            <DateInput
              name="start"
              placeholder="Start Date"
              value={query.start}
              max={query.end}
              onChange={handleDayChange}
            />
            <i>to</i>
            <DateInput
              name="end"
              placeholder="End Date"
              value={query.end}
              min={query.start}
              onChange={handleDayChange}
            />
            <input type="submit" style={{ opacity: 0, position: "absolute" }} />
          </div>
          <div className="right-side">
            <div className="group">
              <ToggleLinksInput
                name="period"
                options={[
                  { text: "Month", value: "month" },
                  { text: "Week", value: "week" },
                  { text: "Day", value: "day" },
                ]}
                className="border-right"
              />
            </div>
          </div>
        </div>

        <div className="Chart-Frame bar-chart">
          <div className="head">
            <div className="right-side">
              <div className="group">
                <ToggleLinksInput
                  name="units"
                  options={[
                    { text: "Kg", value: "metric" },
                    { text: "Lbs", value: "imperial" },
                  ]}
                />
              </div>
            </div>

            <div className="left-side">
              <div className="group">
                <ToggleLinksInput
                  className="tabs"
                  name="type"
                  options={[
                    { text: "Total", value: "total" },
                    { text: "Squat", value: "squat" },
                    { text: "Bench", value: "bench" },
                    { text: "Deadlift", value: "deadlift" },
                  ]}
                />
              </div>
            </div>
          </div>

          <div className="body">
            {!_.isEmpty(selectedMetrics) && (
              <div>
                <div className="legend">
                  <div className="bar series raw">Raw Volume</div>
                  <div className="bar series equipped">Equipped Volume</div>
                </div>
                <div className="axis-labels">
                  <div className="left">Volume</div>
                </div>
                <div className="chart-fullsize">
                  <div className="labels">
                    <Chart
                      type="Bar"
                      only="label"
                      data={{ series: volumeSeries }}
                      options={{
                        width: "60px",
                        height: "380px",
                        stackBars: true,
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          showGrid: false,
                          showLabel: true,
                          scaleMinSpace: 30,
                          onlyInteger: true,
                          low: 0,
                          high: maxVolume,
                        },
                      }}
                    />
                  </div>

                  <div className="data">
                    <Chart
                      type="Bar"
                      data={{
                        labels: labels,
                        series: volumeSeries,
                      }}
                      options={{
                        width: `${_.size(keys) * 40}px`,
                        height: "380px",
                        chartPadding: { left: 35 },
                        stackBars: true,
                        axisX: { showGrid: false, showLabel: true },
                        axisY: {
                          showGrid: true,
                          showLabel: false,
                          scaleMinSpace: 30,
                          low: 0,
                          high: maxVolume,
                        },
                      }}
                      tooltip={(item, i) => {
                        const key = keys[i];
                        const date = moment
                          .utc(query.start, DATE_PICKER_FORMAT)
                          .add(key, period);
                        const dayRange = getDayRangeString(
                          [
                            date,
                            date.clone().add(1, period).subtract(1, "day"),
                          ],
                          query
                        );
                        const rawTotal = _.round(rawSeries.data[i] || 0, 1);
                        const equippedTotal = _.round(
                          equippedSeries.data[i] || 0,
                          1
                        );
                        return (
                          <div className="tooltip" style={{ left: item.x }}>
                            <div className="details">
                              <div>
                                <b>{dayRange}</b>
                              </div>
                              <div>
                                Raw <b>{locals.withWeight(rawTotal, units)}</b>
                              </div>
                              <div>
                                Equipped{" "}
                                <b>{locals.withWeight(equippedTotal, units)}</b>
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <MetricsFoot
          supportHref="/dashboard/support/videos?video=IQQwEQj8ctw"
          shareHref="/actions/refresh?modal[type]=share"
        />
      </form>
    </div>
  );
}

function handleDayChange() {
  submit("#_", { body: document.getElementById("metrics-form") });
}

const RawEquippedVolumeSplit = (app) => {
  app.get(
    "/raw-equipped-volume-split",
    set("heading", "Raw & Equipped Volume Split"),
    async ({ req, res, load, locals, api, fail }) => {
      const { query } = req;

      _.defaults(req.query, {
        type: "total",
        period: "day",
        units: locals.units,
        start: moment.utc().startOf("month").format(DATE_PICKER_FORMAT),
        end: moment.utc().endOf("month").format(DATE_PICKER_FORMAT),
      });

      await Promise.all([
        load("exercisesVolumeSplitRawEquipped", {
          start: moment
            .utc(query.start, DATE_PICKER_FORMAT)
            .startOf("day")
            .toDate(),
          end: moment.utc(query.end, DATE_PICKER_FORMAT).endOf("day").toDate(),
          period: query.period,
          proxyAthlete: query.athlete,
          reqSource: "metrics",
        }),
      ]);

      const modal = _.get(query, "modal", {});

      if (modal.type === "share" && !modal.snapshot) {
        // create share link
        try {
          modal.snapshot = (
            await (
              await api("snapshot", {
                method: "POST",
                body: {
                  user: {
                    name: locals.athlete.name,
                    registered: locals.athlete.registered,
                    avatar: locals.athlete.avatar,
                    units: locals.athlete.units,
                  },
                  type: "raw-equipped-volume-split",
                  data: JSON.stringify(
                    _.chain(locals)
                      .pick(["exercisesVolumeSplitRawEquipped"])
                      .assign({
                        period: query.period,
                        start: query.start,
                        end: query.end,
                      })
                      .value()
                  ),
                },
              })
            ).json()
          )._id;

          modal.date = getDayRangeString([
            moment.utc(query.start, DATE_PICKER_FORMAT),
            moment.utc(query.end, DATE_PICKER_FORMAT),
          ]);
          return res.refreshWith({ modal });
        } catch (e) {
          console.error(e.stack || e);
          fail(400, "Error creating share link.");
        }
      }

      locals.modal = {
        share: <ShareModal />,
        "activity-feed": <ActivityDrawer />,
      }[modal.type];

      res.body = <View />;
    }
  );
};

export default RawEquippedVolumeSplit;
